import { useState } from 'react';
import { formatStampCount, getStampStackImagePath } from '../../utils/stampUtils';
import CardModal from './CardModal';

/**
 * SpotCard - スポットの画像・番号・スタンプ数を表示するカード
 *
 * 画像構成:
 *   stamp-stack … スタンプ積み上げ（スタンプ数に応じて出し分け、0枚時は非表示）
 *                 /images/common/stamp-stack/stamp-{1|5|10|20|25}.png
 *   card-1.png  … カード画像（左前面）
 *   card-2.png  … カード画像（右前面）
 *   spot-number-{id}.png … 番号アイコン
 *
 * スタンプ積み上げ表示ロジック（1枚 = 10スタンプ）:
 *   0スタンプ        → 非表示
 *   1〜49スタンプ    → stamp-1.png
 *   50〜99スタンプ   → stamp-5.png
 *   100〜199スタンプ → stamp-10.png
 *   200〜249スタンプ → stamp-20.png
 *   250スタンプ〜    → stamp-25.png
 *
 * @param {{ spot: { id: string|number, name: string, stampCount: number }, campaignId: string }} props
 */
const SpotCard = ({ spot, campaignId, displayIndex }) => {
  const spotDirId = `${campaignId}_${spot.id}`;
  const basePath = `/images/rallies/${campaignId}/spots/${spotDirId}`;

  const stampStackPath = getStampStackImagePath(spot.stampCount, campaignId);

  // モーダル管理: { src, alt } | null
  const [modalImage, setModalImage] = useState(null);

  const handleCardClick = (src, alt) => {
    // 画像が存在しない（display:none）場合は無視
    setModalImage({ src, alt });
  };

  return (
    <div>
      {/* 拡大モーダル */}
      {modalImage && (
        <CardModal
          imageSrc={modalImage.src}
          altText={modalImage.alt}
          onClose={() => setModalImage(null)}
        />
      )}

      {/* 画像エリア（スタンプ積み上げもここに絶対配置） */}
      <div className="relative w-full" style={{ aspectRatio: '1 / 1.3' }}>
        {/* スタンプ積み上げ: 左下に絶対配置。透明余白を -m で打ち消しつつカード下端まで伸ばす */}
        {stampStackPath && (
          <img
            src={stampStackPath}
            alt={`スタンプ ${spot.stampCount}個`}
            className="absolute bottom-0 left-0 w-[82%] object-contain object-bottom"
            style={{ marginBottom: '0%', marginLeft: '-10%' }}
            aria-hidden="true"
          />
        )}
        {/* card-1（上）・card-2（下）: 右寄り・底辺揃え、クリックで拡大 */}
        <div className="absolute bottom-0 right-[4%] w-[35%] flex flex-col gap-0.5">
          <img
            src={`${basePath}/card-1.png`}
            alt={`${spot.name} カード1`}
            className="w-full object-contain drop-shadow cursor-pointer"
            onClick={(e) => handleCardClick(e.target.src, `${spot.name} カード1`)}
            onError={(e) => {
              if (e.target.src.endsWith('.png')) {
                e.target.src = `${basePath}/card-1.jpg`;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
          <img
            src={`${basePath}/card-2.png`}
            alt={`${spot.name} カード2`}
            className="w-full object-contain drop-shadow cursor-pointer"
            onClick={(e) => handleCardClick(e.target.src, `${spot.name} カード2`)}
            onError={(e) => {
              if (e.target.src.endsWith('.png')) {
                e.target.src = `${basePath}/card-2.jpg`;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        </div>
      </div>

      {/* スポット情報（番号アイコン・ニックネーム・スタンプ数）: 画像エリアとは独立したflexレイアウト */}
      <div className="flex items-center gap-2 mt-1">
        <img
          src={`/images/rallies/${campaignId}/spot-number-${displayIndex}.png`}
          alt={`${displayIndex}`}
          className="w-11 h-11 object-contain flex-shrink-0"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-bold text-gray-800">{spot.name}</span>
          <span className="text-xl font-bold" style={{ color: '#950100' }}>
            {formatStampCount(spot.stampCount)}
            <span className="text-sm font-bold ml-0.5">スタンプ</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default SpotCard;
